import React, { useRef, useState, useEffect, useContext, useCallback, useMemo } from 'react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { InputTextarea } from 'primereact/inputtextarea';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Toast } from 'primereact/toast';
import axios from 'axios';
import { BarrioContext } from '../components/BarrioContext';
import { useAuth } from '../components/AuthContext';
import LoadingOverlay from '../components/LoadingOverlay';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import esLocale from '@fullcalendar/core/locales/es';

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmtFecha = (fechaStr) => {
    if (!fechaStr) return '';
    const d = new Date(fechaStr);
    const hoy = new Date();
    const manana = new Date(hoy); manana.setDate(hoy.getDate() + 1);
    const ayer = new Date(hoy); ayer.setDate(hoy.getDate() - 1);
    const base = d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
    if (d.toDateString() === hoy.toDateString()) return `Hoy — ${base}`;
    if (d.toDateString() === manana.toDateString()) return `Mañana — ${base}`;
    if (d.toDateString() === ayer.toDateString()) return `Ayer — ${base}`;
    return base.charAt(0).toUpperCase() + base.slice(1);
};

const fmtHorario = (ini, fin) => {
    if (!ini && !fin) return 'Todo el día';
    if (ini && !fin) return `Desde las ${ini}`;
    if (!ini && fin) return `Hasta las ${fin}`;
    return `${ini} – ${fin}`;
};

const timeToDate = (str) => {
    if (!str) return null;
    const [h, m] = str.split(':');
    const d = new Date(); d.setHours(Number(h), Number(m), 0, 0);
    return d;
};

const dateToTime = (d) => {
    if (!d) return '';
    return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const addHour = (t) => {
    if (!t) return '01:00';
    const [h, m] = t.split(':').map(Number);
    return `${String(Math.min(h + 1, 23)).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
};

const isPropia = (reserva, usuarioId) => reserva.usuarioId === Number(usuarioId);

const isPasada = (fechaStr) => {
    const d = new Date(fechaStr);
    d.setHours(23, 59, 59);
    return d < new Date();
};

// ── Estilos ───────────────────────────────────────────────────────────────────

const estadoBadge = (estado) => {
    const map = {
        Confirmada: { bg: '#ecfdf3', color: '#12b76a', border: '#a9efc5' },
        Cancelada:  { bg: '#fef3f2', color: '#f04438', border: '#fecdca' },
    };
    const s = map[estado] || { bg: '#f2f4f7', color: '#667085', border: '#d0d5dd' };
    return (
        <span style={{
            background: s.bg, color: s.color, border: `1px solid ${s.border}`,
            fontWeight: 600, fontSize: '0.72rem', padding: '0.15rem 0.55rem',
            borderRadius: 99, whiteSpace: 'nowrap',
        }}>
            {estado}
        </span>
    );
};

// ── Componente principal ──────────────────────────────────────────────────────

const initialForm = {
    reservaId:  0,
    salonId:    null,
    fecha:      null,
    horaInicio: null,   // Date object (timeOnly)
    horaFin:    null,   // Date object (timeOnly)
    motivo:     '',
};

const ReservarSum = () => {
    const toast        = useRef(null);
    const HOST_API     = process.env.REACT_APP_URL;
    const { barrio }   = useContext(BarrioContext);
    const { administracionId } = useAuth();

    const user        = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
    const usuarioId   = user.id || user.usuarioId || 0;
    const solicitante = [user.nombre, user.apellido].filter(Boolean).join(' ') || 'Usuario';

    const [reservas,  setReservas]  = useState([]);
    const [salones,   setSalones]   = useState([]);
    const [loading,   setLoading]   = useState(false);
    const [visible,   setVisible]   = useState(false);
    const [saving,    setSaving]    = useState(false);
    const [formData,  setFormData]  = useState(initialForm);

    // ── Filtro de vista ──
    const [filtroFecha,      setFiltroFecha]      = useState(null);
    const [vistaCalendario,  setVistaCalendario]  = useState(false);

    const eventosCalendario = useMemo(() => reservas.map(r => {
        const fecha     = r.fecha?.substring(0, 10) ?? '';
        const todoElDia = !r.horaInicio && !r.horaFin;
        const esMia     = r.usuarioId === Number(usuarioId);
        const color     = r.estado === 'Cancelada' ? '#f04438' : esMia ? '#1570ef' : '#6b7280';
        return {
            id:              String(r.reservaId),
            title:           r.salonNombre,
            start:           todoElDia ? fecha : `${fecha}T${r.horaInicio || '00:00'}`,
            end:             todoElDia ? fecha : `${fecha}T${r.horaFin || addHour(r.horaInicio)}`,
            allDay:          todoElDia,
            backgroundColor: color,
            borderColor:     color,
            extendedProps:   { reserva: r, esMia },
        };
    }), [reservas, usuarioId]);

    const hc = (campo, valor) => setFormData(prev => ({ ...prev, [campo]: valor }));

    // ── Fetch reservas ──
    const fetchReservas = useCallback(async () => {
        if (!barrio?.id) return;
        setLoading(true);
        try {
            const { data } = await axios.get(
                `${HOST_API}api/reservasSUM/GetReservas?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setReservas(data);
        } catch {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar las reservas', life: 3000 });
        } finally {
            setLoading(false);
        }
    }, [barrio?.id, HOST_API, administracionId]);

    // ── Fetch salones para el dropdown del form ──
    const fetchSalones = useCallback(async () => {
        if (!barrio?.id) return;
        try {
            const { data } = await axios.get(
                `${HOST_API}api/salones/GetSalones?administracionId=${administracionId}&barrioId=${barrio.id}`
            );
            setSalones(data);
        } catch { /* silencioso */ }
    }, [barrio?.id, HOST_API, administracionId]);

    useEffect(() => { fetchReservas(); fetchSalones(); }, [fetchReservas, fetchSalones]);

    // ── Abrir form nueva reserva ──
    const handleNueva = () => {
        setFormData(initialForm);
        setVisible(true);
    };

    // ── Submit ──
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.salonId) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná un espacio', life: 3000 });
            return;
        }
        if (!formData.fecha) {
            toast.current.show({ severity: 'warn', summary: 'Atención', detail: 'Seleccioná una fecha', life: 3000 });
            return;
        }

        const payload = {
            reservaId:        formData.reservaId,
            salonId:          formData.salonId,
            fecha:            formData.fecha.toISOString(),
            horaInicio:       dateToTime(formData.horaInicio),
            horaFin:          dateToTime(formData.horaFin),
            motivo:           formData.motivo,
            barrioId:         barrio.id,
            administracionId,
            usuarioId,
            solicitante,
            estado:           'Confirmada',
        };

        setSaving(true);
        try {
            await axios.post(`${HOST_API}api/reservasSUM/SaveReserva`, payload);
            setVisible(false);
            fetchReservas();
            toast.current.show({ severity: 'success', summary: 'Reserva confirmada', detail: 'Tu reserva fue registrada correctamente', life: 3000 });
        } catch (err) {
            const msg = err.response?.data?.errorDisplay || 'No se pudo guardar la reserva';
            toast.current.show({ severity: err.response?.status === 409 ? 'warn' : 'error', summary: err.response?.status === 409 ? 'Conflicto de horario' : 'Error', detail: msg, life: 4000 });
        } finally {
            setSaving(false);
        }
    };

    // ── Cancelar reserva ──
    const handleCancelar = (r) => {
        confirmDialog({
            message: `¿Cancelar la reserva del ${new Date(r.fecha).toLocaleDateString('es-AR')} en "${r.salonNombre}"?`,
            header: 'Cancelar reserva',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Sí, cancelar',
            rejectLabel: 'No',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                try {
                    await axios.put(`${HOST_API}api/reservasSUM/CancelReserva?reservaId=${r.reservaId}&administracionId=${administracionId}&usuarioId=${usuarioId}`);
                    fetchReservas();
                    toast.current.show({ severity: 'info', summary: 'Cancelada', detail: 'La reserva fue cancelada', life: 2500 });
                } catch {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudo cancelar la reserva', life: 3000 });
                }
            },
        });
    };

    // ── Filtrar reservas para mostrar ──
    const reservasFiltradas = reservas.filter(r => {
        if (!filtroFecha) return true;
        const df = filtroFecha.toISOString().substring(0, 10);
        return new Date(r.fecha).toISOString().substring(0, 10) === df;
    });

    const proximas  = reservasFiltradas.filter(r => !isPasada(r.fecha));
    const pasadas   = reservasFiltradas.filter(r =>  isPasada(r.fecha));

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />

            <LoadingOverlay visible={loading} />

            {/* ── Header ── */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '1rem', marginBottom: '1.5rem' }}>
                <div>
                    <h4 style={{ margin: 0, color: '#101828' }}>Reservas</h4>
                    <p style={{ margin: '0.3rem 0 0', color: '#667085', fontSize: '0.88rem' }}>
                        Reservás a nombre de <strong>{solicitante}</strong>
                    </p>
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
                    {/* Toggle vista */}
                    <div className="flex" style={{ border: '1px solid #d0d5dd', borderRadius: 8, overflow: 'hidden' }}>
                        <Button
                            icon="pi pi-list"
                            label="Lista"
                            onClick={() => setVistaCalendario(false)}
                            className={vistaCalendario ? 'p-button-text' : ''}
                            style={{ borderRadius: 0, border: 'none', padding: '0.5rem 0.9rem' }}
                        />
                        <Button
                            icon="pi pi-calendar"
                            label="Calendario"
                            onClick={() => setVistaCalendario(true)}
                            className={!vistaCalendario ? 'p-button-text' : ''}
                            style={{ borderRadius: 0, border: 'none', padding: '0.5rem 0.9rem' }}
                        />
                    </div>
                    {!vistaCalendario && (
                        <Calendar
                            value={filtroFecha}
                            onChange={(e) => setFiltroFecha(e.value)}
                            dateFormat="dd/mm/yy"
                            placeholder="Filtrar por fecha"
                            showIcon
                            showButtonBar
                            className="p-inputtext-sm"
                            style={{ width: 200 }}
                        />
                    )}
                    <Button label="Nueva reserva" icon="pi pi-plus" onClick={handleNueva} disabled={!barrio?.id} />
                </div>
            </div>

            {/* ── Vista Calendario ── */}
            {vistaCalendario ? (
                <div style={{ background: '#fff', borderRadius: 12, padding: '1rem', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                    <div style={{ fontSize: '0.78rem', color: '#667085', marginBottom: '0.75rem', display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
                        <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#1570ef', marginRight:5 }}/>Mi reserva</span>
                        <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#6b7280', marginRight:5 }}/>Otra reserva</span>
                        <span><span style={{ display:'inline-block', width:10, height:10, borderRadius:'50%', background:'#f04438', marginRight:5 }}/>Cancelada</span>
                    </div>
                    <FullCalendar
                        plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                        initialView="timeGridWeek"
                        locale={esLocale}
                        firstDay={1}
                        headerToolbar={{
                            left:   'prev,next today',
                            center: 'title',
                            right:  'dayGridMonth,timeGridWeek,timeGridDay',
                        }}
                        buttonText={{ today: 'Hoy', month: 'Mes', week: 'Semana', day: 'Día' }}
                        allDayText="Todo el día"
                        noEventsText="Sin reservas"
                        slotMinTime="06:00:00"
                        slotMaxTime="23:00:00"
                        slotLabelFormat={{ hour: '2-digit', minute: '2-digit', hour12: false }}
                        events={eventosCalendario}
                        height="auto"
                        eventContent={(info) => {
                            const r = info.event.extendedProps.reserva;
                            const esMia = info.event.extendedProps.esMia;
                            const isMonth = info.view.type === 'dayGridMonth';
                            return (
                                <div style={{ padding: isMonth ? '1px 4px' : '3px 6px', fontSize: isMonth ? '0.71rem' : '0.78rem', overflow: 'hidden', lineHeight: 1.35, cursor: 'default' }}>
                                    <div style={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        {r.salonNombre}
                                    </div>
                                    {!isMonth && (
                                        <>
                                            <div style={{ opacity: 0.92 }}>
                                                {r.solicitante}{esMia ? ' (yo)' : ''}
                                            </div>
                                            {r.motivo && (
                                                <div style={{ opacity: 0.75, fontStyle: 'italic', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                                    {r.motivo}
                                                </div>
                                            )}
                                        </>
                                    )}
                                </div>
                            );
                        }}
                    />
                </div>
            ) : (
                <>
                    {/* ── Próximas ── */}
                    <Section titulo="Próximas reservas" cantidad={proximas.length}>
                        {proximas.length === 0
                            ? <Empty texto="No hay reservas próximas" />
                            : proximas.map(r => (
                                <ReservaCard
                                    key={r.reservaId}
                                    reserva={r}
                                    esMia={isPropia(r, usuarioId)}
                                    onCancelar={handleCancelar}
                                />
                            ))
                        }
                    </Section>

                    {/* ── Historial ── */}
                    {pasadas.length > 0 && (
                        <Section titulo="Historial" cantidad={pasadas.length} secundaria>
                            {pasadas.map(r => (
                                <ReservaCard key={r.reservaId} reserva={r} esMia={isPropia(r, usuarioId)} pasada />
                            ))}
                        </Section>
                    )}
                </>
            )}

            {/* ── Dialog nueva reserva ── */}
            <Dialog
                header="Nueva reserva"
                visible={visible}
                style={{ width: '460px' }}
                breakpoints={{ '960px': '100vw' }}
                modal
                onHide={() => setVisible(false)}
            >
                <form onSubmit={handleSubmit}>
                    <div className="flex flex-column gap-3 mt-2">

                        <div>
                            <label style={labelStyle}>Espacio <span style={req}>*</span></label>
                            <Dropdown
                                value={formData.salonId}
                                options={salones}
                                optionLabel="nombre"
                                optionValue="salonId"
                                onChange={(e) => hc('salonId', e.value)}
                                placeholder="Seleccioná el espacio"
                                className="p-inputtext-sm w-full"
                                filter
                                emptyMessage="No hay espacios disponibles"
                            />
                            {salones.find(s => s.salonId === formData.salonId)?.capacidad && (
                                <span style={{ fontSize: '0.78rem', color: '#667085', marginTop: '0.25rem', display: 'block' }}>
                                    Capacidad: {salones.find(s => s.salonId === formData.salonId).capacidad} personas
                                </span>
                            )}
                        </div>

                        <div>
                            <label style={labelStyle}>Fecha <span style={req}>*</span></label>
                            <Calendar
                                value={formData.fecha}
                                onChange={(e) => hc('fecha', e.value)}
                                dateFormat="dd/mm/yy"
                                minDate={new Date()}
                                showIcon
                                className="p-inputtext-sm w-full"
                                placeholder="Seleccioná la fecha"
                            />
                        </div>

                        <div style={{ display: 'flex', gap: '0.75rem' }}>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Hora inicio <span style={opt}>(opcional)</span></label>
                                <Calendar
                                    value={formData.horaInicio}
                                    onChange={(e) => hc('horaInicio', e.value)}
                                    timeOnly
                                    hourFormat="24"
                                    showIcon
                                    className="p-inputtext-sm w-full"
                                    placeholder="HH:mm"
                                />
                            </div>
                            <div style={{ flex: 1 }}>
                                <label style={labelStyle}>Hora fin <span style={opt}>(opcional)</span></label>
                                <Calendar
                                    value={formData.horaFin}
                                    onChange={(e) => hc('horaFin', e.value)}
                                    timeOnly
                                    hourFormat="24"
                                    showIcon
                                    className="p-inputtext-sm w-full"
                                    placeholder="HH:mm"
                                />
                            </div>
                        </div>

                        <div>
                            <label style={labelStyle}>Motivo <span style={opt}>(opcional)</span></label>
                            <InputTextarea
                                value={formData.motivo}
                                onChange={(e) => hc('motivo', e.target.value)}
                                rows={3}
                                autoResize
                                className="w-full"
                                placeholder="Ej: Reunión de propietarios, cumpleaños, etc."
                                maxLength={300}
                            />
                        </div>

                        <div className="flex justify-content-end gap-2 mt-1">
                            <Button label="Cancelar" type="button" severity="secondary" text onClick={() => setVisible(false)} />
                            <Button
                                label={saving ? 'Guardando…' : 'Confirmar reserva'}
                                icon={saving ? undefined : 'pi pi-check'}
                                type="submit"
                                disabled={saving}
                            />
                        </div>

                    </div>
                </form>
            </Dialog>
        </>
    );
};

// ── Sub-componentes ───────────────────────────────────────────────────────────

const Section = ({ titulo, cantidad, secundaria, children }) => (
    <div style={{ marginBottom: '2rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
            <h5 style={{ margin: 0, color: secundaria ? '#667085' : '#344054', fontWeight: 600 }}>{titulo}</h5>
            {cantidad > 0 && (
                <span style={{
                    background: secundaria ? '#f2f4f7' : '#eff8ff',
                    color: secundaria ? '#667085' : '#1570ef',
                    fontSize: '0.75rem', fontWeight: 600,
                    padding: '0.1rem 0.5rem', borderRadius: 99,
                }}>
                    {cantidad}
                </span>
            )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
            {children}
        </div>
    </div>
);

const Empty = ({ texto }) => (
    <div style={{
        background: '#f9fafb', border: '1px dashed #e4e7ec',
        borderRadius: 12, padding: '1.5rem', textAlign: 'center',
        color: '#98a2b3', fontSize: '0.88rem',
    }}>
        {texto}
    </div>
);

const ReservaCard = ({ reserva: r, esMia, onCancelar, pasada }) => {
    const cancelable = esMia && !pasada && r.estado !== 'Cancelada';

    return (
        <div style={{
            background: pasada ? '#fafafa' : '#fff',
            border: `1px solid ${r.estado === 'Cancelada' ? '#fecdca' : esMia ? '#b2ddff' : '#e4e7ec'}`,
            borderLeft: `4px solid ${r.estado === 'Cancelada' ? '#f04438' : esMia ? '#1570ef' : '#d0d5dd'}`,
            borderRadius: 12,
            padding: '1rem 1.1rem',
            boxShadow: pasada ? 'none' : '0 1px 3px rgba(0,0,0,0.04)',
            opacity: pasada || r.estado === 'Cancelada' ? 0.7 : 1,
            display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem',
        }}>
            <div style={{ flex: 1 }}>
                {/* Salón + estado */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.3rem', flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, color: '#101828', fontSize: '0.95rem' }}>
                        {r.salonNombre}
                    </span>
                    {estadoBadge(r.estado)}
                    {esMia && r.estado !== 'Cancelada' && (
                        <span style={{ fontSize: '0.72rem', color: '#1570ef', fontWeight: 600 }}>Mi reserva</span>
                    )}
                </div>

                {/* Fecha */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: '0.2rem' }}>
                    <i className="pi pi-calendar" style={{ fontSize: '0.8rem', color: '#667085' }} />
                    <span style={{ fontSize: '0.85rem', color: '#344054' }}>{fmtFecha(r.fecha)}</span>
                </div>

                {/* Horario */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginBottom: r.motivo ? '0.3rem' : 0 }}>
                    <i className="pi pi-clock" style={{ fontSize: '0.8rem', color: '#667085' }} />
                    <span style={{ fontSize: '0.85rem', color: '#344054' }}>{fmtHorario(r.horaInicio, r.horaFin)}</span>
                </div>

                {/* Motivo */}
                {r.motivo && (
                    <p style={{ margin: '0.25rem 0 0', fontSize: '0.82rem', color: '#667085', fontStyle: 'italic' }}>
                        "{r.motivo}"
                    </p>
                )}

                {/* Solicitante (solo si no es mía) */}
                {!esMia && (
                    <p style={{ margin: '0.3rem 0 0', fontSize: '0.78rem', color: '#98a2b3' }}>
                        Reservado por {r.solicitante}
                    </p>
                )}
            </div>

            {cancelable && (
                <Button
                    icon="pi pi-times"
                    rounded
                    text
                    severity="danger"
                    tooltip="Cancelar reserva"
                    tooltipOptions={{ position: 'left' }}
                    onClick={() => onCancelar(r)}
                    style={{ flexShrink: 0 }}
                />
            )}
        </div>
    );
};

const labelStyle = { display: 'block', marginBottom: '0.35rem', fontWeight: 500, color: '#344054', fontSize: '0.87rem' };
const req        = { color: '#f04438' };
const opt        = { color: '#98a2b3', fontWeight: 400 };

export default ReservarSum;
